import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import * as React from 'react';
import {
  ArrowDown,
  ArrowUp,
  RotateCcw,
  SlidersHorizontalIcon,
} from 'lucide-react';
import { Column, ColumnPinningState, Table } from '@tanstack/react-table';

import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from 'components/ui/dropdown-menu';

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
  triggerClassName?: string;
  ordering?: boolean;
  defaultColumnPinning?: ColumnPinningState;
}

export function DataTableViewOptions<TData>({
  table,
  triggerClassName,
  ordering = false,
  defaultColumnPinning,
}: DataTableViewOptionsProps<TData>) {
  const [open, setOpen] = React.useState(false);

  const hideableColumns = table
    .getAllColumns()
    .filter(
      (column) =>
        typeof column.accessorFn !== 'undefined' && column.getCanHide()
    );

  const allVisible = hideableColumns.every((column) => column.getIsVisible());

  const getColumnLabel = (column: Column<TData, unknown>) => {
    const meta = column.columnDef.meta as { label?: string } | undefined;
    return meta?.label ?? column.id;
  };

  const defaultLeft = defaultColumnPinning?.left ?? [];
  const defaultRight = defaultColumnPinning?.right ?? [];

  const pinnableColumns = table
    .getAllLeafColumns()
    .filter((column) => column.getCanPin());
  const pinningEnabled = pinnableColumns.length > 0;
  const { left: currentLeft = [], right: currentRight = [] } =
    table.getState().columnPinning;

  const pinningChanged =
    pinningEnabled &&
    (currentLeft.length !== defaultLeft.length ||
      currentRight.length !== defaultRight.length ||
      !defaultLeft.every((id, index) => currentLeft[index] === id) ||
      !defaultRight.every((id, index) => currentRight[index] === id));

  const orderChanged = table.getState().columnOrder.length > 0;

  const moveColumn = (columnId: string, direction: -1 | 1) => {
    const storedOrder = table.getState().columnOrder;
    const currentOrder = storedOrder.length
      ? storedOrder
      : table.getAllLeafColumns().map((column) => column.id);
    const currentIndex = currentOrder.indexOf(columnId);
    const targetIndex = currentIndex + direction;
    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= currentOrder.length
    ) {
      return;
    }
    const nextOrder = [...currentOrder];
    const [moved] = nextOrder.splice(currentIndex, 1);
    nextOrder.splice(targetIndex, 0, moved);
    table.setColumnOrder(nextOrder);
  };

  const orderedHideable = (() => {
    const currentOrder = table.getState().columnOrder.length
      ? table.getState().columnOrder
      : table.getAllLeafColumns().map((column) => column.id);
    const leafColumnsById = new Map(
      table.getAllLeafColumns().map((column) => [column.id, column])
    );
    return currentOrder
      .map((id) => leafColumnsById.get(id))
      .filter((column) => column !== undefined) as Array<
      (typeof hideableColumns)[number]
    >;
  })();

  const resetColumns = () => {
    hideableColumns.forEach((column) => column.toggleVisibility(true));
    table.setColumnOrder([]);
    if (pinningEnabled) {
      table.setColumnPinning({
        left: [...defaultLeft],
        right: [...defaultRight],
      });
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild className="focus-visible:ring-primary-500">
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'hidden h-8 font-GilroySemibold lg:flex',
            triggerClassName
          )}
        >
          <SlidersHorizontalIcon className="mr-2 h-4 w-4" />
          Colunas
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[420px] w-[240px] overflow-y-auto bg-white"
      >
        <DropdownMenuLabel>Exibir colunas</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableColumns.map((column) => {
          return (
            <DropdownMenuCheckboxItem
              key={column.id}
              className="font-sans capitalize"
              checked={column.getIsVisible()}
              onCheckedChange={(value) => column.toggleVisibility(!!value)}
            >
              {getColumnLabel(column)}
            </DropdownMenuCheckboxItem>
          );
        })}
        {ordering && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Ordenar colunas</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {orderedHideable.map((column, index) => (
              <div
                key={column.id}
                className="flex items-center justify-between gap-2 px-2 py-1"
              >
                <span className="font-sans text-sm capitalize truncate">
                  {getColumnLabel(column)}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Mover ${getColumnLabel(column)} para cima`}
                    disabled={index === 0}
                    onClick={() => moveColumn(column.id, -1)}
                    className="h-7 w-7 p-0"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Mover ${getColumnLabel(column)} para baixo`}
                    disabled={index === orderedHideable.length - 1}
                    onClick={() => moveColumn(column.id, 1)}
                    className="h-7 w-7 p-0"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </div>
            ))}
          </>
        )}
        {pinningEnabled && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Fixar colunas</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {pinnableColumns.map((column) => (
              <DropdownMenuCheckboxItem
                key={column.id}
                className="font-sans capitalize"
                checked={column.getIsPinned() === 'left'}
                onCheckedChange={(value) => column.pin(value ? 'left' : false)}
              >
                {getColumnLabel(column)}
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="font-sans"
          disabled={allVisible && !orderChanged && !pinningChanged}
          onSelect={resetColumns}
        >
          <RotateCcw className="mr-2 h-3.5 w-3.5" />
          Resetar colunas
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
